import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";

const tidy = (v) => String(v || "").trim().replace(/\s+/g, " ");
const key = (v) => tidy(v).toLowerCase();

// Free-text field that suggests values already in use and snaps to the existing
// spelling when the typed text differs only by case or spacing.
export const SuggestInput = ({
  value = "",
  onChange,
  suggestions = [],
  placeholder,
  testId,
  className = "rounded-sm pr-9",
}) => {
  const [open, setOpen] = useState(false);

  const matches = useMemo(() => {
    const needle = key(value);
    const pool = [...new Set(suggestions.map(tidy).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b),
    );
    if (!needle) return pool;
    return pool.filter((s) => key(s).includes(needle) && key(s) !== needle);
  }, [suggestions, value]);

  const commit = () => {
    const typed = tidy(value);
    if (!typed) {
      if (typed !== value) onChange(typed);
      return;
    }
    const existing = suggestions.map(tidy).find((s) => key(s) === key(typed));
    onChange(existing || typed);
  };

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          commit();
          window.setTimeout(() => setOpen(false), 150);
        }}
        placeholder={placeholder}
        autoComplete="off"
        className={className}
        data-testid={testId}
      />
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      {open && matches.length > 0 && (
        <div
          className="absolute inset-x-0 top-[42px] z-40 max-h-48 overflow-y-auto border border-slate-300 bg-white shadow-lg"
          data-testid={`${testId}-options`}
        >
          <p className="border-b border-slate-100 px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-slate-400">
            Already in use
          </p>
          {matches.map((item) => (
            <button
              key={item}
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                onChange(item);
                setOpen(false);
              }}
              data-testid={`${testId}-option-${item}`}
              className="block w-full truncate px-4 py-2.5 text-left text-[13px] text-slate-900 transition-colors duration-150 hover:bg-slate-50"
            >
              {item}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
